/**
 * Charging-rate resolver — pure functions, no React.
 *
 * `chargingRates` lives on `settings.charging_rates` and has the shape:
 *   { home: ProfileConfig, work: ProfileConfig }
 *
 * ProfileConfig:
 *   { profile_type: "flat"|"tiered"|"tou"|"monthly",
 *     flat_rate_per_kwh?: number,
 *     tou_periods?: [{ id, label, rate, start, end, days, color }] }
 *
 * For an MVP we implement Flat (single rate) and TOU (per-time-of-day rate).
 * Tiered + Monthly are accepted as profile_type values but not yet
 * computable — callers fall back to "no rate configured" UX.
 *
 * NOTE on TOU + the charge log: a charging session is logged with a DATE
 * only, not a time. We resolve the active TOU period using the **current
 * client time** at the moment the user picks the location — same heuristic
 * Apple Charging / Tesla apps use ("off-peak now"). Users logging delayed
 * sessions can edit Cost manually.
 */

export const LOCATION_VALUES = ["home", "work", "other"];

export const MONTH_OPTIONS = [
  { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
  { value: 4, label: "April" }, { value: 5, label: "May" }, { value: 6, label: "June" },
  { value: 7, label: "July" }, { value: 8, label: "August" }, { value: 9, label: "September" },
  { value: 10, label: "October" }, { value: 11, label: "November" }, { value: 12, label: "December" },
];

const MONTH_LABEL = Object.fromEntries(MONTH_OPTIONS.map((m) => [m.value, m.label]));

export const DEFAULT_TOU_PERIOD = () => ({
  id: cryptoId(),
  label: "Off-Peak",
  rate: "",
  start: "23:00",
  end: "07:00",
  days: "all",   // "all" | "weekdays" | "weekends"
  color: "green",
});

export const DEFAULT_TIER = (isLast = false) => ({
  id: cryptoId(),
  threshold: isLast ? null : "",   // last tier = "Remaining" (null)
  rate: "",
});

function cryptoId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `tou_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
}

/** Default profile shape used when nothing is configured yet. */
export const emptyProfile = () => ({
  profile_type: "flat",
  flat_rate_per_kwh: "",
  tou_periods: [],
  // Tiered profile defaults: 2 tiers (one numeric threshold + the "Remaining"
  // tier) covering the full calendar year.
  tiers: [],
  tier_start_month: 1,
  tier_end_month: 12,
  // Monthly subscription
  monthly_cost: "",
  monthly_start_date: "",
  monthly_end_date: "",
});

/** Parse a YYYY-MM-DD entry-date string to a 1-12 month index. */
export const monthOfIsoDate = (s) => {
  if (!s || typeof s !== "string") return null;
  const m = /^(\d{4})-(\d{1,2})-/.exec(s);
  return m ? parseInt(m[2], 10) : null;
};

/** True iff a numeric month falls in [start, end] with wrap-around for ranges that cross year-end. */
export const monthInRange = (mo, start, end) => {
  if (!mo) return false;
  const s = Number(start), e = Number(end);
  if (!Number.isFinite(s) || !Number.isFinite(e)) return false;
  if (s <= e) return mo >= s && mo <= e;
  // Wrap: e.g. Nov→Feb = months 11,12,1,2
  return mo >= s || mo <= e;
};

const DAYS_WEEKDAY = new Set([1, 2, 3, 4, 5]);
const DAYS_WEEKEND = new Set([0, 6]);

const minutesOf = (hhmm) => {
  if (!hhmm || typeof hhmm !== "string") return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  if (isNaN(h) || isNaN(mm) || h > 23 || mm > 59) return null;
  return h * 60 + mm;
};

const isInWindow = (now, start, end) => {
  // Both numbers in [0, 1440). If end < start the window crosses midnight.
  if (start === end) return false;
  if (start < end) return now >= start && now < end;
  return now >= start || now < end;
};

const matchesDay = (days, weekday) => {
  if (days === "all" || !days) return true;
  if (days === "weekdays") return DAYS_WEEKDAY.has(weekday);
  if (days === "weekends") return DAYS_WEEKEND.has(weekday);
  return true;
};

/**
 * Resolve the rate (in $/kWh) for a configured location at a specific moment.
 * Returns { rate, period, profile_type } when a rate is resolvable; `null`
 * when no rate is configured (or profile type isn't computable yet).
 */
export function resolveRate(profile, when = new Date()) {
  if (!profile || !profile.profile_type) return null;
  const t = profile.profile_type;
  if (t === "flat") {
    const r = parseFloat(profile.flat_rate_per_kwh);
    if (!Number.isFinite(r) || r <= 0) return null;
    return { rate: r, period: null, profile_type: "flat" };
  }
  if (t === "tou") {
    const periods = Array.isArray(profile.tou_periods) ? profile.tou_periods : [];
    const weekday = when.getDay();
    const nowMin = when.getHours() * 60 + when.getMinutes();
    for (const p of periods) {
      const s = minutesOf(p.start);
      const e = minutesOf(p.end);
      const rate = parseFloat(p.rate);
      if (s == null || e == null || !Number.isFinite(rate) || rate <= 0) continue;
      if (!matchesDay(p.days, weekday)) continue;
      if (isInWindow(nowMin, s, e)) {
        return { rate, period: p, profile_type: "tou" };
      }
    }
    return null;
  }
  // tiered / monthly — these are not "instant rate" profiles in the same way
  // as flat/tou, so resolveRate returns null. Use computeChargeCost for the
  // actual numeric cost (which knows about kWh-tier accumulation and the
  // monthly subscription model).
  return null;
}

/**
 * Total cost for a tiered profile given total kWh added in this session.
 * Sums per-tier prices cumulatively. Returns null when no valid tiers.
 */
function computeTieredCost(profile, kwh) {
  const tiers = Array.isArray(profile.tiers) ? profile.tiers : [];
  if (!tiers.length) return null;
  let remaining = kwh;
  let total = 0;
  for (let i = 0; i < tiers.length; i++) {
    if (remaining <= 0) break;
    const t = tiers[i];
    const rate = parseFloat(t.rate);
    if (!Number.isFinite(rate) || rate < 0) return null;
    const isLast = i === tiers.length - 1;
    const threshold = isLast ? null : parseFloat(t.threshold);
    if (!isLast && (!Number.isFinite(threshold) || threshold <= 0)) return null;
    // Previous cumulative ceiling (== threshold of tier i-1, or 0).
    const prevCeil = i === 0 ? 0 : parseFloat(tiers[i - 1].threshold) || 0;
    const tierCapacity = isLast ? Infinity : Math.max(0, threshold - prevCeil);
    const used = Math.min(remaining, tierCapacity);
    total += used * rate;
    remaining -= used;
  }
  return Math.round(total * 100) / 100;
}

/**
 * Compute cost for a session.
 *   - flat / tou → kWh × rate
 *   - monthly → 0 (subscription model — flat fee handled at aggregate level)
 *   - tiered → cumulative per-tier price (if entry-month is in range)
 *
 * `when` is used for TOU resolution; `entryDate` (YYYY-MM-DD) is used for the
 * tiered month-range check. Returns null when the cost cannot be computed.
 */
export function computeChargeCost({ profile, kwh, when, entryDate }) {
  if (!profile || !profile.profile_type) return null;
  const t = profile.profile_type;

  if (t === "monthly") {
    const mc = parseFloat(profile.monthly_cost);
    if (!Number.isFinite(mc) || mc < 0) return null;
    return { cost: 0, period: null, profile_type: "monthly", monthly_cost: mc };
  }

  if (t === "tiered") {
    const n = parseFloat(kwh);
    if (!Number.isFinite(n) || n <= 0) return null;
    const mo = entryDate ? monthOfIsoDate(entryDate) : new Date().getMonth() + 1;
    if (!monthInRange(mo, profile.tier_start_month, profile.tier_end_month)) {
      return { cost: null, out_of_range: true, profile_type: "tiered" };
    }
    const c = computeTieredCost(profile, n);
    if (c == null) return null;
    return { cost: c, period: null, profile_type: "tiered" };
  }

  const r = resolveRate(profile, when);
  if (!r) return null;
  const n = parseFloat(kwh);
  if (!Number.isFinite(n) || n <= 0) return null;
  return { cost: Math.round(n * r.rate * 100) / 100, ...r };
}

/**
 * Short summary shown in the Settings header chip:
 *   "Flat · $0.12/kWh"  or  "TOU · $0.07–$0.14"
 */
export function summarizeProfile(profile) {
  if (!profile || !profile.profile_type) return null;
  const t = profile.profile_type;
  if (t === "flat") {
    const r = parseFloat(profile.flat_rate_per_kwh);
    if (!Number.isFinite(r) || r <= 0) return null;
    return { label: "Flat", detail: `$${r.toFixed(2)}/kWh` };
  }
  if (t === "tou") {
    const rates = (profile.tou_periods || [])
      .map((p) => parseFloat(p.rate))
      .filter((x) => Number.isFinite(x) && x > 0);
    if (!rates.length) return { label: "TOU", detail: "—" };
    const min = Math.min(...rates);
    const max = Math.max(...rates);
    if (min === max) return { label: "TOU", detail: `$${min.toFixed(2)}/kWh` };
    return { label: "TOU", detail: `$${min.toFixed(2)}–$${max.toFixed(2)}` };
  }
  if (t === "tiered") {
    const tiers = (profile.tiers || []).filter((x) => parseFloat(x.rate) > 0);
    if (!tiers.length) return { label: "Tiered", detail: "—" };
    const rates = tiers.map((x) => parseFloat(x.rate));
    const min = Math.min(...rates), max = Math.max(...rates);
    const range =
      min === max ? `$${min.toFixed(2)}/kWh` : `$${min.toFixed(2)}–$${max.toFixed(2)}`;
    return { label: "Tiered", detail: range };
  }
  if (t === "monthly") {
    const mc = parseFloat(profile.monthly_cost);
    if (!Number.isFinite(mc) || mc < 0) return { label: "Monthly", detail: "—" };
    return { label: "Monthly", detail: `$${mc.toFixed(2)}/mo` };
  }
  return null;
}

/** Pretty label for the inline hint under the location selector. */
export function rateHintFor({ locationTag, profile, when, entryDate }) {
  if (locationTag === "other") {
    return { tone: "muted", text: "Public chargers vary — enter cost manually." };
  }
  if (!profile) {
    return { tone: "muted", text: "No rate configured. Open Settings → Charging Rates to set one." };
  }
  if (profile.profile_type === "monthly") {
    const mc = parseFloat(profile.monthly_cost);
    if (Number.isFinite(mc) && mc >= 0) {
      return { tone: "ok", text: `Monthly plan · $${mc.toFixed(2)}/mo — sessions don't add to cost.` };
    }
    return { tone: "muted", text: "Monthly plan configured but cost not set — enter manually." };
  }
  if (profile.profile_type === "tiered") {
    const mo = entryDate ? monthOfIsoDate(entryDate) : new Date().getMonth() + 1;
    if (!monthInRange(mo, profile.tier_start_month, profile.tier_end_month)) {
      const s = MONTH_LABEL[profile.tier_start_month] || "Jan";
      const e = MONTH_LABEL[profile.tier_end_month] || "Dec";
      return { tone: "warn", text: `Tiered profile applies ${s}–${e} only — enter cost manually for this date.` };
    }
    const tiers = (profile.tiers || []).filter((x) => parseFloat(x.rate) > 0);
    if (!tiers.length) {
      return { tone: "muted", text: "Tiered profile has no rates yet — enter manually." };
    }
    return { tone: "ok", text: `Tiered · ${tiers.length} tier${tiers.length > 1 ? "s" : ""} · auto-calculates from kWh.` };
  }
  const r = resolveRate(profile, when);
  if (!r) {
    return { tone: "muted", text: "Configured profile has no active rate right now — enter manually." };
  }
  if (r.profile_type === "flat") {
    return { tone: "ok", text: `Flat · $${r.rate.toFixed(2)}/kWh` };
  }
  // tou
  const p = r.period;
  const window = p ? ` · ${formatHour(p.start)} – ${formatHour(p.end)}` : "";
  const labelLower = (p?.label || "Off-Peak").toLowerCase();
  return {
    tone: "ok",
    text: `Time-of-Use · ${labelLower} now ($${r.rate.toFixed(2)}/kWh${window})`,
  };
}

function formatHour(hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm || "");
  if (!m) return hhmm;
  let h = parseInt(m[1], 10);
  const mm = m[2];
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return mm === "00" ? `${h} ${ampm}` : `${h}:${mm} ${ampm}`;
}
